/**
 * Image delivery helpers.
 *
 * Supabase Storage public URLs can be served through the image transform
 * endpoint (`/render/image/public/...`) with width + quality params, which
 * returns a resized, WebP-negotiated image instead of the full-size original.
 * That is the single biggest feed win on slow networks: feed tiles only ever
 * need ~600-1200px, not a 4000px phone photo.
 *
 * Any non-Storage URL is passed through untouched.
 */

const OBJECT_PATH = "/storage/v1/object/public/";
const RENDER_PATH = "/storage/v1/render/image/public/";

export function optimizedImage(url: string, width: number, quality = 70): string {
  if (!url || !url.includes(OBJECT_PATH)) return url;
  const base = url.split("?")[0]!.replace(OBJECT_PATH, RENDER_PATH);
  return `${base}?width=${width}&quality=${quality}&resize=cover`;
}

/** Responsive candidates for a feed/gallery image. */
export function imageSrcSet(url: string, widths: number[], quality = 70): string | undefined {
  if (!url || !url.includes(OBJECT_PATH)) return undefined;
  return widths.map((w) => `${optimizedImage(url, w, quality)} ${w}w`).join(", ");
}

/** True when the device asked us to conserve data, or is on a 2g-class link. */
export function isSaveData(): boolean {
  if (typeof navigator === "undefined") return false;
  const c = (navigator as { connection?: { saveData?: boolean; effectiveType?: string } }).connection;
  if (!c) return false;
  return c.saveData === true || c.effectiveType === "slow-2g" || c.effectiveType === "2g";
}
